/**
 * @author        yontaek@zumper.com (Yon Taek Chung)
 * @copyright     Copyright (c) 2021, Zumper
 * @description   email validator
 */

import {
  type APPLY,
  type MESSAGE,
  TOUR,
  type WAITLIST,
} from '@zcode/nr/es/shared/constants/message'
import { isBefore, isEqual, setHours, setMilliseconds, setMinutes, setSeconds } from 'date-fns'
import * as Yup from 'yup'

const selectedTimeValidator = Yup.date().test({
  message: 'please choose a valid time',
  test: (date) => {
    if (date) {
      const today = new Date()

      return !isBefore(date, today)
    }
    return true
  },
})

const selectedDateValidator = Yup.string().test({
  message: 'please choose a valid date',
  test: (dateString) => {
    if (dateString) {
      const date = new Date(dateString)
      const today = new Date()

      const dateToTest = setHours(setMinutes(setSeconds(setMilliseconds(date, 0), 0), 0), 0)
      const todayToTest = setHours(setMinutes(setSeconds(setMilliseconds(today, 0), 0), 0), 0)

      return !isBefore(date, today) || isEqual(dateToTest, todayToTest)
    }
    return true
  },
})

export interface TourTimeValidatorProps {
  equalCTAIntent: typeof APPLY | typeof MESSAGE | typeof TOUR | typeof WAITLIST | undefined
  fetchingTourAvailability: boolean
  tourTimeOptionsByDate: ReadonlyMap<ReturnType<Date['toDateString']>, readonly Date[]> | undefined
}

export const getSelectedDateTimeValidator = ({
  equalCTAIntent,
  fetchingTourAvailability,
  tourTimeOptionsByDate,
}: TourTimeValidatorProps) => {
  if (equalCTAIntent === TOUR) {
    return Yup.object()
      .required()
      .shape({
        selectedDate: selectedDateValidator.required().test({
          message: 'please choose an available date',
          test: (dateString) => {
            if (fetchingTourAvailability) {
              return false
            }
            return (
              !!dateString &&
              dateString !== 'Invalid Date' &&
              // @ts-expect-error `string` isn't assignable to "Sun ..." | ...
              !!tourTimeOptionsByDate?.has(dateString)
            )
          },
        }),
        selectedTime: selectedTimeValidator.required().test({
          message: 'please choose an available time',
          test: (date) => {
            if (fetchingTourAvailability || !date || !tourTimeOptionsByDate) {
              return false
            }
            for (const validDates of tourTimeOptionsByDate.values()) {
              if (validDates.some((validDate) => isEqual(date, validDate))) {
                return true
              }
            }
            return false
          },
        }),
      })
  }
  return Yup.object().nullable().shape({
    selectedDate: selectedDateValidator,
    selectedTime: selectedTimeValidator,
  })
}
